import { Injectable } from "@angular/core";
import { Observable, catchError, finalize, first, map, of, shareReplay, tap, throwError } from "rxjs";
import { AppConfig } from "src/app/app-config.service";
import { PagedResponse } from "src/app/shared/models/paged-response.model";
import { Tool } from "../models/tool.model";
import { ToolPreview } from "../models/tool-preview.model";
import { ToolProductTeam } from "../models/tool-product-team.model";
import { OAuthToken } from "../models/oauth-token.model";
import { ToolsRequest } from "../models/tools-request.model";
import { DevPortalHttpClient } from "src/app/shared/services/dev-portal-http-client.service";
import { ToolUpsertRequest } from "../models/create-tool-request.model";
import { ToolName } from "../models/tool-name.model";

@Injectable()
export class ToolService {
    private _devPortalHttpClient: DevPortalHttpClient;

    constructor( devPortalHttpClient: DevPortalHttpClient) {
        this._devPortalHttpClient = devPortalHttpClient;
    }

    getToolNames(): Observable<ToolName[]> {
        return this._devPortalHttpClient.Get<ToolName[]>(`/tools/names`)
            .pipe(
                first(),
                catchError(error => {
                    console.error("Error fetching tool names:", error);
                    return throwError(() => error);
                })
            );
    }
    
    getTools(request: ToolsRequest = {}): Observable<PagedResponse<ToolPreview>> {
        const params = {
            pageNumber: request.pageNumber,
            pageSize: request.pageSize,
            keywords: request.keywords,
            personas: request.personas,
            lifeCyclePhases: request.lifeCyclePhases,
            productTeams: request.productTeams,
            toolTypes: request.toolTypes,
            statuses: [1], // Enable active tools by default
        };

        const url = "/tools?" + this.formatParameters(params);

        return this._devPortalHttpClient.Get<PagedResponse<ToolPreview>>(url)
            .pipe(
                first(),
                map(response => {
                    // Validate the response before processing
                    if (!('data' in response && 'pageNumber' in response && 'pageSize' in response && 'totalCount' in response && 'totalPages' in response)) {
                        throw new Error("Invalid response: " + JSON.stringify(response));
                    }

                    const processedData = response.data.map(t => {
                        return {
                            ...t,
                            imageUrl: t.previewReference
                                ? `${AppConfig.settings.cdnUrl}/tools-preview/${t.id}/${t.previewReference}`
                                : undefined,
                        };
                    });

                    return { 
                        data: processedData, 
                        pageNumber: response.pageNumber, 
                        pageSize: response.pageSize,
                        totalCount: response.totalCount,
                        totalPages: response.totalPages
                    };
                }),
                catchError(error => {
                    console.error("Error fetching tools:", error);
                    return throwError(() => error);
                })
            );
    }

    getToolById(ToolId: number): Observable<Tool | null> {
        return this._devPortalHttpClient.Get<Tool>(`/tools/details?id=${ToolId}`)
            .pipe(
                first(),
                map(response => {
                    return response as Tool;
                }),
                catchError(error => {
                    if( error.status === 404 ) {
                        return of(null); // Return null observable if 404 error
                    }
                    console.error("Error fetching Tool by ID:", error);
                    return throwError(() => error);
                })
            );
    }

    deleteToolById(ToolId: number): Observable<void> {
        return this._devPortalHttpClient.Delete<void>(`/tools/${ToolId}`)
            .pipe(
                first(),
                catchError(error => {
                    console.error("Error deleting Tool by ID:", error);
                    return throwError(() => error);
                })
            );
    }

    createTool(request: ToolUpsertRequest): Observable<Tool> {
        console.log("Creating Tool with request:", request);
        return this._devPortalHttpClient.Post<ToolUpsertRequest, Tool>(`/tools`, request)
            .pipe(
                first(),
                catchError(error => {
                    console.error("Error creating Tool:", error);
                    return throwError(() => error);
                })
            );
    }

    updateTool(ToolId: number, request: ToolUpsertRequest): Observable<Tool> {
        console.log("Updating Tool with request:", request);
        return this._devPortalHttpClient.Put<ToolUpsertRequest, Tool>(`/tools/${ToolId}`, request)
            .pipe(
                first(),
                catchError(error => {
                    console.error("Error updating Tool:", error);
                    return throwError(() => error);
                })
            );
    }

    getProductTeams(): Observable<ToolProductTeam[]> {
        return this._devPortalHttpClient.Get<ToolProductTeam[]>(`/tools/product-teams`)
            .pipe(
                first(),
                catchError(error => {
                    console.error("Error fetching product teams:", error);
                    return throwError(() => error);
                })
            );
    }

    private tokenValue: OAuthToken | null = null;
    private tokenCache: Observable<OAuthToken> | null = null;
    private readonly imageTokenRefreshBufferMs = 5 * 60 * 1000;
    
    getImageStorageAccountTokenAsync(): Observable<OAuthToken> {
        const cached = this.tokenValue;
        if (cached) {
            const expMs = Date.parse(String(cached.expiration ?? ''));
            const hasValidExp = !Number.isNaN(expMs);
            const stillValid = hasValidExp && (expMs - Date.now()) > this.imageTokenRefreshBufferMs;
            if (stillValid) {
                return of(cached);
            }
        }

        // De-dupe concurrent refreshes.
        if (this.tokenCache) {
            return this.tokenCache;
        }

        this.tokenCache = this._devPortalHttpClient.Get<OAuthToken>(`/tools/token`)
            .pipe(
                first(),
                tap(token => {
                    this.tokenValue = token;
                }),
                shareReplay({ bufferSize: 1, refCount: false }),
                catchError(error => {
                    console.error("Error fetching token:", error);
                    this.tokenValue = null;
                    return throwError(() => error);
                }),
                finalize(() => {
                    this.tokenCache = null;
                })
            );

        return this.tokenCache;
    }

    private formatParameters(params: Record<string, any>): string {
        const filteredParams = Object.keys(params)
            .filter(key => params[key] !== undefined)
            .reduce((obj: { [key: string]: any }, key) => {
                obj[key] = params[key];
                return obj;
            }, {});

        const searchParams = new URLSearchParams();
        
        Object.entries(filteredParams).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                searchParams.append(key, value.join(','));
            } else {
                searchParams.append(key, value);
            }
        });
        
        return searchParams.toString();
    }
}
